import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import './MaintenanceGate.css';

const MAINTENANCE_BYPASS_KEY = 'incrypt_maintenance_bypass';

const isMaintenanceModeEnabled = () => {
  return process.env.REACT_APP_MAINTENANCE_MODE === 'true';
};

const getAdminBypassPath = () => {
  return (
    process.env.REACT_APP_MAINTENANCE_ADMIN_PATH ||
    '/incrypt-maintenance-admin'
  );
};

const isMaintenanceBypassed = () => {
  return sessionStorage.getItem(MAINTENANCE_BYPASS_KEY) === 'true';
};

const MaintenancePage = () => {
  return (
    <main className="maintenance-gate">
      <section className="maintenance-card" aria-labelledby="maintenance-title">
        <p className="maintenance-eyebrow">
          <span className="maintenance-pulse" aria-hidden="true" />
          Scheduled maintenance
        </p>
        <h1 id="maintenance-title" className="maintenance-title">
          We&rsquo;ll be right back
        </h1>
        <p className="maintenance-copy">
          Incrypt is currently undergoing a planned upgrade while we move a few
          important pieces behind the scenes. Access is temporarily paused so we
          can keep account data consistent during the transition.
        </p>
        <p className="maintenance-copy">
          Thanks for bearing with us. The site will be back online shortly.
        </p>
        <div className="maintenance-footer">
          Please avoid refreshing checkout or account actions until maintenance
          is complete.
        </div>
      </section>
    </main>
  );
};

interface MaintenanceGateProps {
  children: ReactNode;
}

export const MaintenanceGate = ({ children }: MaintenanceGateProps) => {
  const location = useLocation();
  const maintenanceMode = isMaintenanceModeEnabled();
  const adminBypassPath = getAdminBypassPath();
  const isAdminBypassRoute = location.pathname === adminBypassPath;

  useEffect(() => {
    if (maintenanceMode && isAdminBypassRoute) {
      sessionStorage.setItem(MAINTENANCE_BYPASS_KEY, 'true');
    }

    if (!maintenanceMode) {
      sessionStorage.removeItem(MAINTENANCE_BYPASS_KEY);
    }
  }, [isAdminBypassRoute, maintenanceMode]);

  if (!maintenanceMode) {
    return <>{children}</>;
  }

  /* The 2FA-reset request and its emailed cancel link stay open during maintenance,
     as their endpoints do on the server (middleware/maintenance.js): a member can be
     locked out, or cancelling somebody else's request, while the site is down. */
  if (location.pathname.startsWith('/account/2fa-reset')) {
    return <>{children}</>;
  }

  if (isAdminBypassRoute) {
    return <Navigate to="/login" replace />;
  }

  if (isMaintenanceBypassed()) {
    return <>{children}</>;
  }

  return <MaintenancePage />;
};

